/**
 * SmartEatingFeaturePage — public info page for each of the 7 differentiating
 * Nutramilo modules surfaced in the landing nav "Умно хранене" dropdown.
 *
 * URL: /smart/:slug
 * Slugs: plate-simulator, digital-twin, meal-planner, ask-ai,
 *        epigenetic-map, clinical-analysis, digital-lab
 *
 * Content sourced from the real product diary (Дневник — Nutramilo, Feb 2026)
 * — capabilities, sub-features, and example outputs reflect what each module
 * actually does in the app. All BG strings in clean Bulgarian (no transliteration).
 *
 * Bilingual BG/EN, light/dark via existing ThemeContext.
 */
import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { SubpageNav, SubpageFooter } from "@/components/SubpageLayout";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import {
  Utensils, Activity, ClipboardList, Bot, Dna, Stethoscope,
  FlaskRound, ArrowRight, Check, Sparkles,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// Content registry — real capabilities from the production diary.
// Images: hand-picked Unsplash editorials matching each module's domain.
// ─────────────────────────────────────────────────────────────────────
const FEATURES = {
  "health-intelligence": {
    icon: Activity,
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "Цялостен метаболитен дашборд", en: "Unified metabolic dashboard" },
    title: { bg: "Health Intelligence", en: "Health Intelligence" },
    subtitle: {
      bg: "Едно живо табло с цялото ти здраве: телесен състав, метаболитни маркери, тренди от дневника, лабораторни и AI прозрения с peer-reviewed цитати.",
      en: "One live board with your whole health: body composition, metabolic markers, diary trends, lab results and AI insights with peer-reviewed citations.",
    },
    bullets: [
      { bg: "Биологична възраст (PhenoAge на Levine 2018) + gap спрямо хронологичната",
        en: "Biological age (Levine 2018 PhenoAge) + gap vs. chronological age" },
      { bg: "VAIS — Visceral Adiposity Index Score: висцерална мазнина без MRI скенер",
        en: "VAIS — Visceral Adiposity Index Score: visceral fat estimate without an MRI scan" },
      { bg: "BMI, талия, бедра, WHR, телесен състав и тяхната седмична динамика",
        en: "BMI, waist, hips, WHR, body composition and their weekly trajectory" },
      { bg: "Top Driver на седмицата: AI открива кой фактор тежи най-силно (сън, стрес, DIL, активност)",
        en: "Weekly Top Driver: AI identifies which factor matters most this week (sleep, stress, DIL, activity)" },
      { bg: "Sync с Nutrition Lab — закачените храни и Col3 Tracker се появяват като плочки на дашборда",
        en: "Synced with Nutrition Lab — pinned foods and Col3 Tracker appear as live tiles on the dashboard" },
      { bg: "Седмични AI прозрения (Observation → Interpretation → Action) с PubMed PMID цитати",
        en: "Weekly AI insights (Observation → Interpretation → Action) with PubMed PMID citations" },
      { bg: "Save-to-PMOS бутон: запазва snapshot от всеки модул (диета, симулатор, скрининги) в твоето здравно досие",
        en: "Save-to-PMOS button: snapshots from every module (diet, simulator, screenings) feed into your health record" },
    ],
    steps: [
      { bg: "Логваш данни — измервания, лаборатории, хранителен дневник, симптоми",
        en: "Log data — measurements, labs, food diary, symptoms" },
      { bg: "Дашбордът автоматично оживява с твоите скорове, тренди и Top Driver",
        en: "The dashboard auto-populates with your scores, trends and Top Driver" },
      { bg: "Получаваш AI прозрения и конкретно действие за следващите 7 дни",
        en: "Get AI insights and a concrete action for the next 7 days" },
    ],
  },

  "nutrition-lab": {
    icon: FlaskRound,
    image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "Откривателен инструмент за метаболитно хранене", en: "Metabolic food discovery engine" },
    title: { bg: "Nutrition Intelligence Lab", en: "Nutrition Intelligence Lab" },
    subtitle: {
      bg: "Над 20 000 храни, оценени чрез собствения Food Match Score (FMS) спрямо твоите състояния, фенотипи и Nutramilo индекси. С един клик GPT-5.2 ти генерира 7-дневно AI меню само от готвени ястия.",
      en: "20,000+ foods scored by our own Food Match Score (FMS) against your conditions, phenotypes and Nutramilo indices. With one click GPT-5.2 generates a 7-day AI menu of cooked dishes.",
    },
    bullets: [
      { bg: "56 здравословни състояния — вкл. PCOS, ендометриоза, NAFLD, тромбофилия, синдром на Кушинг, Търнър, Клайнфелтър, подагра, остеопороза, ревматоиден артрит, депресия, паник атаки, киста на щитовидната жлеза и още",
        en: "56 health conditions — incl. PCOS, endometriosis, NAFLD, thrombophilia, Cushing's, Turner, Klinefelter, gout, osteoporosis, rheumatoid arthritis, depression, panic attacks, thyroid cyst and more" },
      { bg: "Food Match Score (FMS) — динамично оценява всяка храна спрямо състояние × фенотип × диета × индекси × нутриенти",
        en: "Food Match Score (FMS) — dynamic scoring per food vs. condition × phenotype × diet × indices × nutrients" },
      { bg: "9 диетични модела: Средиземноморска, DASH, MIND, нисковъглехидратна, Keto, Палео, Нордическа, Растителна, Противовъзпалителна",
        en: "9 diet patterns: Mediterranean, DASH, MIND, low-carb, Keto, Paleo, Nordic, Plant-based, Anti-inflammatory" },
      { bg: "Nutramilo индекси: DIL (Holt 1997), NIS v1.1.9, Inflammation Index, COL3 (синтез на колаген), Калорийна плътност (Volumetrics на Rolls 2009)",
        en: "Nutramilo indices: DIL (Holt 1997), NIS v1.1.9, Inflammation Index, COL3 (collagen synthesis), Calorie Density (Rolls 2009 Volumetrics)" },
      { bg: "KEGG метаболитни пътища — visualization кои биологични пътища са засегнати + храни-модулатори с peer-reviewed база",
        en: "KEGG metabolic pathways — visualises which biological pathways are affected + food modulators with peer-reviewed evidence" },
      { bg: "AI Меню с GPT-5.2 — 7-дневен план за храна само от готвени ястия и пълни рецепти, точно от избраните от теб храни",
        en: "AI Menu by GPT-5.2 — 7-day plan of cooked dishes and full recipes, built only from foods you picked" },
      { bg: '"Към меню" чекбокс на всяка храна + история на менютата ("Моите AI менюта")',
        en: '"For menu" checkbox on every food + AI menu history ("My AI Menus")' },
      { bg: "Формули в прозрачен изглед — DIL, NIS, Inflammation и Калорийна плътност с PMID референции",
        en: "Transparent Formulas modal — DIL, NIS, Inflammation and Calorie Density with PMID references" },
    ],
    steps: [
      { bg: "Избираш твоите здравословни състояния (1 или повече) и предпочитани диети",
        en: "Pick your health conditions (one or more) and preferred diets" },
      { bg: "FMS engine класира 20 000+ храни — топ препоръките са с предимство към готвени и пълни рецепти",
        en: "The FMS engine ranks 20,000+ foods — top picks are biased toward cooked dishes and full recipes" },
      { bg: 'Маркираш храните "Към меню" → GPT-5.2 генерира 7-дневно меню точно от тях за под 90 секунди',
        en: 'Tick foods "For menu" → GPT-5.2 generates a 7-day menu only from those, in under 90 seconds' },
    ],
  },

  "plate-simulator": {
    icon: Utensils,
    image: "https://images.unsplash.com/photo-1607532941433-304659e8198a?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "Метаболитна обратна връзка в реално време", en: "Real-time metabolic feedback" },
    title: { bg: "Симулатор на чинията", en: "Plate Simulator" },
    subtitle: {
      bg: "Преди да хапнете — виждате точно как чинията влияе на инсулина, ситостта и гликемичния товар. Корекции в реално време чрез AI агентите.",
      en: "Before you eat — see how the plate affects insulin, satiety, and glycaemic load. Real-time tweaks via our AI agents.",
    },
    bullets: [
      { bg: "Изчислява DIL (Инсулинов товар) и NIS% (Nutramilo Insulin Score) за всяка комбинация от храни",
        en: "Computes DIL (Insulin Load) and NIS% (Nutramilo Insulin Score) for any food combination" },
      { bg: "Прогноза за 4-часовия инсулинов отговор — пик, време до пик, експозиция (AUC)",
        en: "4-hour insulin-response forecast — peak value, time-to-peak, exposure (AUC)" },
      { bg: "Корекция спрямо HOMA-IR (+50% при HOMA-IR ≥ 5.0) и забавен инсулинов клирънс",
        en: "Adjusted for HOMA-IR (+50% if HOMA-IR ≥ 5.0) and delayed insulin clearance" },
      { bg: "Показва ситост (0–100), теглото на чинията в грамове и разпределение на макронутриентите",
        en: "Shows satiety (0–100), plate weight in grams, and macronutrient distribution" },
      { bg: "Multi-Bot AI обяснение — EndoBot (ендокринология), NutriBot (хранене), NephroBot (бъбреци), HemoBot (кръв)",
        en: "Multi-Bot AI explanation — EndoBot (endocrine), NutriBot (nutrition), NephroBot (renal), HemoBot (blood)" },
      { bg: "Auto-Fix: при висок DIL автоматично предлага суап на съставка с по-нисък инсулинов отговор",
        en: "Auto-Fix: when DIL is high, automatically suggests swapping an ingredient for a lower-impact one" },
    ],
    steps: [
      { bg: "Добавете храните в чинията — чрез снимка, гласово търсене или избор от каталога",
        en: "Add foods to the plate — via photo, voice search, or browsing the catalogue" },
      { bg: "Получавате DIL, NIS%, ситост и макро-баланс веднага — без забавяне",
        en: "Get DIL, NIS%, satiety, and macro balance instantly — no delay" },
      { bg: "Multi-Bot AI обяснява механизмите и предлага 3–5 точни корекции (precision actions)",
        en: "Multi-Bot AI explains the mechanisms and offers 3–5 precision actions" },
    ],
  },

  "digital-twin": {
    icon: Activity,
    image: "https://images.unsplash.com/photo-1554475901-4538ddfbccc2?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "Симулация на вашата физиология", en: "Simulation of your physiology" },
    title: { bg: "Digital Twin (Дигитален близнак)", en: "Digital Twin" },
    subtitle: {
      bg: "Вашият метаболитен близнак моделира как промяна в храненето, хранителните добавки или лекарствата ще промени биомаркерите ви — на 4 часа, 7 дни и 30 дни.",
      en: "Your metabolic twin models how a change in diet, supplements or medications will shift your biomarkers — at 4 hours, 7 days, and 30 days.",
    },
    bullets: [
      { bg: "Прогноза за глюкоза, инсулин, кортизол, кръвно налягане, ситост, енергия, възпаление, съдов тонус",
        en: "Projection for glucose, insulin, cortisol, blood pressure, satiety, energy, inflammation, vascular tone" },
      { bg: "Метаболитна адаптивност и тенденция в телесните мазнини",
        en: "Metabolic adaptability and body-fat trajectory" },
      { bg: "Анализира ефекта на лекарства (напр. Метформин) и хранителни добавки върху прогнозата",
        en: "Models the effect of medications (e.g. Metformin) and supplements on the projection" },
      { bg: 'Режим „Какво ще стане, ако…“: сравнение „текущ режим vs. нов“ с детайлни криви',
        en: '“What-if” mode: side-by-side comparison of current vs. proposed regimen with detailed curves' },
      { bg: 'Optimisation engine: „Начертай ми комбинация от нулата“ — AI избира оптимален stack от добавки',
        en: 'Optimisation engine: “design my stack from scratch” — AI picks an optimal supplement stack' },
      { bg: "Safety check за лекарствени взаимодействия, контраиндикации и бъбречен товар",
        en: "Safety check for drug interactions, contraindications, and renal load" },
    ],
    steps: [
      { bg: "Заредете профил, лаборатории, лекарства и хранителни добавки",
        en: "Load profile, labs, medications, and supplements" },
      { bg: "Промените параметър — добавка, лекарство, хранителен режим или активност",
        en: "Tweak a parameter — supplement, drug, diet, or activity" },
      { bg: "Виждате прогнозата за 4 часа, 7 и 30 дни + attribution за всеки фактор поотделно",
        en: "See the 4-hour, 7-day, and 30-day projection + per-factor attribution" },
    ],
  },

  "meal-planner": {
    icon: ClipboardList,
    image: "https://images.unsplash.com/photo-1493770348161-369560ae357d?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "AI генератор на хранителен план", en: "AI menu generator" },
    title: { bg: "AI Meal Planner", en: "AI Meal Planner" },
    subtitle: {
      bg: "Седмично меню, генерирано според вашия фенотип, DIL цел и хранителни предпочитания. С DIL бюджет за всяко хранене и пазарска листа в един клик.",
      en: "A weekly menu generated for your phenotype, DIL target, and dietary preferences. With a per-meal DIL budget and a shopping list in one click.",
    },
    bullets: [
      { bg: "DIL бюджет за всяко хранене: Закуска ≤ 29, Обяд ≤ 33, Вечеря ≤ 19, Междинно ≤ 10",
        en: "Per-meal DIL budget: Breakfast ≤ 29, Lunch ≤ 33, Dinner ≤ 19, Snack ≤ 10" },
      { bg: "AI избира реално изпълними ястия със здравословни мазнини и пълноценен протеин",
        en: "AI picks realistic dishes with healthy fats and complete protein" },
      { bg: "Зачита алергии, диети (вегетарианска, кето, FODMAP, безглутенова) и култура",
        en: "Respects allergies, diets (vegetarian, keto, FODMAP, gluten-free), and culture" },
      { bg: "Auto-Fix: ако ястие надвишава DIL бюджета — автоматично заменя съставка",
        en: "Auto-Fix: if a meal exceeds the DIL budget, automatically swaps an ingredient" },
      { bg: "Прикачване на PDF/снимка от лекарски преглед — менюто се адаптира спрямо клиничните бележки",
        en: "Attach a PDF/photo from a clinical visit — the menu adapts to the notes" },
      { bg: "Седмичен преглед: реален DIL vs. план, отклонения, дни в зелено/амбер/червено",
        en: "Weekly review: actual DIL vs. plan, deviations, days in green/amber/red" },
    ],
    steps: [
      { bg: "Задавате цел (отслабване, поддръжка, набиране на маса), брой дни и забранени храни",
        en: "Set a goal (loss, maintain, gain), number of days, and excluded foods" },
      { bg: "AI генерира 7-дневно меню с рецепти, грамажи и DIL за всяко ястие",
        en: "AI generates a 7-day menu with recipes, gram weights, and DIL per dish" },
      { bg: "Преглеждате DIL/NIS/ситост за всеки ден и експортирате пазарска листа",
        en: "Review daily DIL/NIS/satiety and export the shopping list" },
    ],
  },

  "ask-ai": {
    icon: Bot,
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "Multi-Bot Health AI", en: "Multi-Bot Health AI" },
    title: { bg: "Питай AI", en: "Ask AI" },
    subtitle: {
      bg: "Четирима специализирани AI агенти — EndoBot, NutriBot, NephroBot и HemoBot — отговарят на вашия въпрос като клиничен консилиум, базиран на peer-reviewed бази с над 30 милиона публикации.",
      en: "Four specialist AI agents — EndoBot, NutriBot, NephroBot, HemoBot — answer your question as a clinical panel, grounded in 30M+ peer-reviewed papers.",
    },
    bullets: [
      { bg: "EndoBot — метаболитна и ендокринна интерпретация (инсулин, кортизол, тироид, полови хормони)",
        en: "EndoBot — metabolic and endocrine interpretation (insulin, cortisol, thyroid, sex hormones)" },
      { bg: "NutriBot — нутригеномика, хранителни взаимодействия и микронутриентен анализ",
        en: "NutriBot — nutrigenomics, food interactions, and micronutrient analysis" },
      { bg: "NephroBot — бъбречен товар, електролити, безопасност при ХБН",
        en: "NephroBot — renal load, electrolytes, CKD safety" },
      { bg: "HemoBot — анемии, желязо, B12, фолат, метилация",
        en: "HemoBot — anaemias, iron, B12, folate, methylation" },
      { bg: "Vector store с над 100 клинични наръчника — източници с PMID + DOI във всеки отговор",
        en: "Vector store with 100+ clinical handbooks — sources with PMID + DOI in every answer" },
      { bg: "Контекст от вашите лаборатории, лекарства и цели — отговорите са персонализирани, не общи",
        en: "Personalised with your labs, medications, and goals — answers are not generic" },
    ],
    steps: [
      { bg: "Питате с текст, глас или снимка (напр. на лабораторен лист)",
        en: "Ask with text, voice, or a photo (e.g. of a lab report)" },
      { bg: "AI оркестратор избира кои агенти да отговорят — единичен или консилиум",
        en: "The AI orchestrator picks which agents respond — single or panel" },
      { bg: "Получавате отговор с цитати и предложени следващи стъпки за дневника",
        en: "Receive an answer with citations and recommended next steps for your diary" },
    ],
  },

  "epigenetic-map": {
    icon: Dna,
    image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "Биологична възраст и метаболитни оси", en: "Biological age and metabolic axes" },
    title: { bg: "Епигенетична карта", en: "Epigenetic Map" },
    subtitle: {
      bg: "Изчислява биологичната ви възраст спрямо хронологичната и визуализира кои ежедневни решения я ускоряват или забавят — без сляпа диета.",
      en: "Computes your biological age vs. chronological and visualises which daily decisions accelerate or slow it — without blind dieting.",
    },
    bullets: [
      { bg: "PhenoAge (Levine 2018) на базата на 9 биомаркера — реална оценка на стареенето на ниво организъм",
        en: "PhenoAge (Levine 2018) based on 9 biomarkers — a real organism-level ageing estimate" },
      { bg: "VAIS — Visceral Adiposity Index Score: оценка на висцералната мазнина без MRI",
        en: "VAIS — Visceral Adiposity Index Score: a visceral-fat estimate without MRI" },
      { bg: "Horizons projection: траектория за 5, 10 и 20 години при различни режими",
        en: "Horizons projection: 5-, 10-, and 20-year trajectory under different regimens" },
      { bg: "Lifestyle attribution: точно кой фактор колко тежи (сън, активност, DIL, стрес, добавки)",
        en: "Lifestyle attribution: exactly which factor weighs how much (sleep, activity, DIL, stress, supplements)" },
      { bg: "Връзка с женска ендокринна фаза, метаболитна гъвкавост и дълголетие на клетките",
        en: "Linked to female endocrine phase, metabolic flexibility, and cellular longevity" },
    ],
    steps: [
      { bg: "Качвате лабораторни резултати (CBC, метаболитен панел, hsCRP)",
        en: "Upload lab results (CBC, metabolic panel, hsCRP)" },
      { bg: "Виждате биологичната си възраст, gap-а спрямо хронологичната и ускорителите/забавителите",
        en: "See your biological age, the gap vs. chronological, and the accelerators/decelerators" },
      { bg: "Получавате персонализиран план за намаляване на gap-а — без обобщени препоръки",
        en: "Receive a personalised plan to shrink the gap — no generic advice" },
    ],
  },

  "clinical-analysis": {
    icon: Stethoscope,
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "Клиничен анализ и онко-метаболитни модули", en: "Clinical analysis and onco-metabolic modules" },
    title: { bg: "Клиничен анализ", en: "Clinical Analysis" },
    subtitle: {
      bg: "Идентифицира доминантния ви метаболитен фенотип (P1–P5) от лабораториите и симптомите, и свързва това с конкретен план за хранене, добавки и проследяване.",
      en: "Identifies your dominant metabolic phenotype (P1–P5) from labs and symptoms, and ties it to a concrete plan for diet, supplements, and follow-up.",
    },
    bullets: [
      { bg: "5 метаболитни фенотипа: инсулинова резистентност, лептин, тироид, кортизол, полови хормони",
        en: "5 metabolic phenotypes: insulin resistance, leptin, thyroid, cortisol, sex hormones" },
      { bg: "10+ скрити модела (hidden patterns): MAFLD, субклиничен хипотиреоидизъм, ранна ХБН, желязна претовареност",
        en: "10+ hidden patterns: MAFLD, subclinical hypothyroidism, early CKD, iron overload" },
      { bg: "Стратификация на риска: CVD (Framingham), Диабет (FINDRISC), гръдна болест ()",
        en: "Risk stratification: CVD (Framingham), Diabetes (FINDRISC), breast risk ()" },
      { bg: "Анализ на дефицити: седмичен среднодневен прием спрямо RDI с препоръчителни корекции",
        en: "Deficit analysis: weekly average intake vs. RDI with recommended corrections" },
      { bg: "Онко-метаболитни модули (само за верифицирани лекари): 10 образователни модула — хиперинсулинемия, IGF-1, Warburg, естрогенен метаболизъм, имунометаболизъм и др.",
        en: "Onco-metabolic modules (verified clinicians only): 10 educational modules — hyperinsulinaemia, IGF-1, Warburg, oestrogen metabolism, immunometabolism, etc." },
      { bg: "Препоръки с цитати от peer-reviewed литература (PMID, DOI)",
        en: "Recommendations with peer-reviewed citations (PMID, DOI)" },
    ],
    steps: [
      { bg: "Качвате лаборатории и попълвате кратък симптомен въпросник",
        en: "Upload labs and fill out a short symptom questionnaire" },
      { bg: "Получавате доминантен фенотип + 3 свързани pattern-а със степен на сигурност",
        en: "Receive a dominant phenotype + 3 associated patterns with confidence levels" },
      { bg: "Виждате конкретен 12-седмичен план за корекция с биомаркерни цели",
        en: "View a concrete 12-week correction plan with biomarker targets" },
    ],
  },

  "digital-lab": {
    icon: FlaskRound,
    image: "https://images.unsplash.com/photo-1579154204601-01588f351e67?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    eyebrow: { bg: "OCR + AI разчитане на лаборатории", en: "OCR + AI lab parsing" },
    title: { bg: "Дигитална лаборатория", en: "Digital Lab" },
    subtitle: {
      bg: "Снимка или PDF на лабораторен лист → разпознати маркери, маркирани отклонения и обяснение от Multi-Bot AI за по-малко от минута.",
      en: "Photo or PDF of a lab report → parsed markers, flagged outliers, and a Multi-Bot AI explanation in under a minute.",
    },
    bullets: [
      { bg: "OCR на над 200 формата лабораторни листове (БГ, ЕС, САЩ)",
        en: "OCR for 200+ lab report formats (BG, EU, US)" },
      { bg: "Автоматично преобразуване на единици (mg/dL ↔ mmol/L) и възрастово/полово коригирани референтни стойности",
        en: "Auto-unit conversion (mg/dL ↔ mmol/L) and age/sex-adjusted reference ranges" },
      { bg: "Исторически тренд и delta спрямо предишни панели — виждате какво се променя",
        en: "Historical trend and delta vs. previous panels — see what's changing" },
      { bg: "Hidden Patterns анализ върху лабораторния панел — детектира субклинични модели",
        en: "Hidden Patterns analysis over the lab panel — detects subclinical patterns" },
      { bg: "Подходящ за пациент и за лекар: SOAP-note ready експорт за специалиста ви",
        en: "Patient- and clinician-friendly: SOAP-note-ready export for your specialist" },
    ],
    steps: [
      { bg: "Снимате или качвате PDF на лабораторен резултат",
        en: "Snap or upload a PDF of a lab result" },
      { bg: "AI извлича стойностите и норми спрямо възраст и пол",
        en: "AI extracts values and norms adjusted for age and sex" },
      { bg: "Виждате обяснение, история, рискове и препоръки на разбираем език",
        en: "See an explanation, history, risks, and recommendations in plain language" },
    ],
  },
};

const VALID_SLUGS = Object.keys(FEATURES);

export default function SmartEatingFeaturePage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  if (!VALID_SLUGS.includes(slug)) {
    return <Navigate to="/" replace />;
  }

  const feat = FEATURES[slug];
  const Icon = feat.icon;
  const localized = (obj) => (bg ? obj.bg : obj.en);

  return (
    <div
      data-testid={`smart-feature-page-${slug}`}
      className={`min-h-screen ${isDark ? "bg-background text-foreground dark" : "bg-[#F9F9F7]"}`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <SEO title={`${localized(feat.title)} · Nutramilo`} description={localized(feat.subtitle)} path={`/smart/${slug}`} />
      <SubpageNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className={`absolute inset-0 ${isDark ? "" : "bg-gradient-to-b from-white to-[#F9F9F7]"}`} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 sm:pt-16 sm:pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div data-testid="smart-feature-eyebrow" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-5 text-xs font-semibold tracking-wide uppercase ${isDark ? "border-[hsl(223,47%,22%)] text-slate-300 bg-[hsl(223,47%,12%)]" : "border-slate-200 text-slate-600 bg-white"}`}>
              <Sparkles className="w-3.5 h-3.5 text-[#e44288]" />
              {localized(feat.eyebrow)}
            </div>
            <div className="flex items-start gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-[#5e227f] to-[#e44288] text-white shadow-lg shadow-[#5e227f]/20">
                <Icon className="w-5 h-5" />
              </span>
              <h1 data-testid="smart-feature-title" className={`text-4xl sm:text-5xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                {localized(feat.title)}
              </h1>
            </div>
            <p data-testid="smart-feature-subtitle" className={`text-lg leading-relaxed max-w-xl ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              {localized(feat.subtitle)}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button data-testid="smart-feature-cta-primary" size="lg" className="brand-cta-primary rounded-full px-6 h-12 text-sm font-semibold">
                  {bg ? "Започни безплатно" : "Start free"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link
                to="/"
                data-testid="smart-feature-cta-secondary"
                className={`text-sm font-medium ${isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-[#287a2e]"}`}
              >
                {bg ? "← Към началната" : "← Back to home"}
              </Link>
            </div>
          </div>

          <div data-testid="smart-feature-image-wrap" className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#5e227f]/10 border border-white/10">
            <img
              src={feat.image}
              alt={localized(feat.title)}
              className="w-full h-[360px] sm:h-[440px] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#5e227f]/30 via-transparent to-[#287a2e]/10 mix-blend-multiply" aria-hidden />
          </div>
        </div>
      </section>

      {/* Capability bullets */}
      <section className={`py-16 ${isDark ? "" : "bg-white"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-[#e44288]" : "text-[#5e227f]"}`}>
            {bg ? "Какво прави" : "What it does"}
          </div>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-10 ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Възможности" : "Capabilities"}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {feat.bullets.map((b, idx) => (
              <div
                key={`bullet-${idx}-${(bg ? b.bg : b.en).slice(0, 16)}`}
                data-testid={`smart-feature-bullet-${idx}`}
                className={`flex gap-3 p-4 rounded-xl border ${isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,8%)]" : "border-slate-200 bg-[#FCFCFA]"}`}
              >
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-[#0e5c60] to-[#68c349] inline-flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-white" />
                </span>
                <p className={`text-sm leading-relaxed ${isDark ? "text-slate-200" : "text-slate-700"}`}>{localized(b)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={`py-16 ${isDark ? "bg-[hsl(223,47%,5%)]" : "bg-[#F9F9F7]"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-[#68c349]" : "text-[#287a2e]"}`}>
            {bg ? "Стъпки" : "Steps"}
          </div>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-10 ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Как работи" : "How it works"}
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {feat.steps.map((s, idx) => (
              <div
                key={`step-${idx}-${(bg ? s.bg : s.en).slice(0, 16)}`}
                data-testid={`smart-feature-step-${idx}`}
                className={`p-6 rounded-2xl border ${isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,9%)]" : "border-slate-200 bg-white"}`}
              >
                <div className={`text-3xl font-bold mb-2 ${isDark ? "text-[#e44288]" : "text-[#5e227f]"}`}>
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <p className={`text-sm leading-relaxed ${isDark ? "text-slate-200" : "text-slate-700"}`}>{localized(s)}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/register">
              <Button data-testid="smart-feature-cta-bottom" size="lg" className="brand-cta-primary rounded-full px-8 h-12 text-sm font-semibold">
                {bg ? "Опитай безплатно" : "Try it free"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SubpageFooter />
    </div>
  );
}
